import { StrisciaGiorni } from "../StrisciaGiorni";
import { comeData, type Candidato } from "../api";

/** Lato più lungo oltre il quale il ritiro è «voluminoso», in cm: lo stesso
 *  ``LATO_VOLUMINOSO_CM`` del backend (``shared/sda_client/ritiri.py``), da
 *  confermare con Bracchi. */
const LATO_VOLUMINOSO_CM = 100;

type Props = {
  selezionati: Candidato[];
  giorni: string[];
  oggi: string;
  giorno: string | null;
  onGiorno: (g: string | null) => void;
  /** Codice (o «da verificare») del ritiro che occupa già il giorno scelto. */
  giaPrenotato: string | null;
  busy: boolean;
  /** Perché il tasto è spento (permessi, dati mancanti); ``null`` = acceso. */
  bloccato: string | null;
  /** «Oggi» si prenota fino a quest'ora (``limite_in_giornata`` del backend). */
  limite: string;
  onPrenota: () => void;
};

const fmt = (n: number, d: number) =>
  n.toLocaleString("it-IT", { minimumFractionDigits: d, maximumFractionDigits: d });

/** Il riquadro a destra: cosa parte, quando, e il tasto. */
export function ProssimoRitiro({
  selezionati,
  giorni,
  oggi,
  giorno,
  onGiorno,
  giaPrenotato,
  busy,
  bloccato,
  limite,
  onPrenota,
}: Props) {
  const colli = selezionati.reduce((s, c) => s + c.colli, 0);
  const peso = selezionati.reduce((s, c) => s + c.peso_kg, 0);
  const volume = selezionati.reduce((s, c) => s + c.volume_m3, 0);
  const lato = (k: "max_l" | "max_p" | "max_a") => Math.max(0, ...selezionati.map((c) => c[k]));
  const l = lato("max_l");
  const p = lato("max_p");
  const a = lato("max_a");
  const voluminoso = Math.max(l, p, a) >= LATO_VOLUMINOSO_CM;
  // ``null`` (nessun giorno scelto, es. il backend non ne propone nessuno) è
  // distinto da «oggi»/nome del giorno: l'etichetta del tasto non deve
  // troncarsi a «Prenota per » con uno spazio penzolante.
  const etichettaGiorno =
    giorno === oggi
      ? "oggi"
      : giorno
        ? comeData(giorno).toLocaleDateString("it-IT", { weekday: "long" })
        : null;
  const motivo =
    bloccato ??
    (selezionati.length === 0
      ? "Spunta almeno un ordine."
      : giorno === null
        ? "Scegli un giorno per il ritiro."
        : null);
  return (
    <aside className="border-l border-line bg-paper p-5 max-lg:border-l-0 max-lg:border-t">
      <h3 className="mb-2 text-[12.5px] font-extrabold text-ink">Prossimo ritiro</h3>
      <p className="flex items-baseline gap-2 text-ink">
        <span className="font-display text-[26px] leading-none font-bold">{colli}</span>
        <span className="text-[12px]">
          {colli === 1 ? "collo" : "colli"} · {selezionati.length}{" "}
          {selezionati.length === 1 ? "ordine" : "ordini"}
        </span>
      </p>
      <dl className="my-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[12px] text-ink">
        <dt>Peso</dt>
        <dd className="text-right tabular-nums tabular-nums">{fmt(peso, 2)} kg</dd>
        <dt>Volume</dt>
        <dd className="text-right tabular-nums tabular-nums">{fmt(volume, 3)} m³</dd>
        <dt>Collo più grande</dt>
        <dd className="text-right tabular-nums tabular-nums">
          {selezionati.length > 0 ? `${fmt(l, 0)}×${fmt(p, 0)}×${fmt(a, 0)} cm` : "—"}
        </dd>
        <dt>Voluminoso</dt>
        <dd className="text-right">{voluminoso ? "sì" : "no"}</dd>
      </dl>
      <p className="mb-1 text-[12px] font-bold text-ink">Giorno del ritiro</p>
      <StrisciaGiorni
        etichetta="Giorno del ritiro"
        giorni={giorni.slice(0, 5)}
        oggi={oggi}
        valore={giorno}
        onChange={onGiorno}
        disabled={busy}
      />
      {giaPrenotato && (
        <p className="mt-2 rounded-lg bg-amber-soft px-2 py-1 text-[12px] text-ink">
          Per questo giorno c'è già il ritiro {giaPrenotato}: il tasto ne aggiunge un secondo.
        </p>
      )}
      <button
        type="button"
        onClick={onPrenota}
        disabled={busy || motivo !== null || giorno === null}
        aria-busy={busy}
        className="mt-3 w-full rounded-full border border-ink bg-ink px-3.5 py-2 text-[13px] font-bold text-on-ink hover:bg-ink/90 disabled:cursor-not-allowed disabled:border-line-2 disabled:bg-paper-2 disabled:text-ink"
      >
        {busy
          ? "Prenotazione in corso…"
          : etichettaGiorno === null
            ? "Prenota"
            : giaPrenotato
              ? `Aggiungi ritiro per ${etichettaGiorno}`
              : `Prenota per ${etichettaGiorno}`}
      </button>
      {motivo && (
        <p className="mt-2 rounded-lg bg-red-soft px-2 py-1 text-[12px] font-semibold text-ink">
          {motivo}
        </p>
      )}
      <p className="mt-2 text-[11.5px] text-ink">
        «Oggi» c'è fino alle {limite}. Il codice arriva subito da SDA; da qui il ritiro non si
        annulla: «Annulla» prepara la mail per Bracchi.
      </p>
    </aside>
  );
}
